(function() {
  // -------------------------
  // CONFIG
  // -------------------------
  const LINE_SPACING_BASE = 1.2;
  const PX_TO_CM = 2.54 / 96;
  const MIN_LINE_HEIGHT_CM = 1.5;
  const MAX_WIDTH_CM = 210;
  const MAX_HEIGHT_CM = 110;
  const BASE_FONT_SIZE = 100;

  let FONT_CONFIG = {};
  let loadedFonts = {};
  let currentFontName = null;

  // ✅ NEU: colorLabel pro line (nur fürs Hidden-Field / properties[FolienfarbeX])
  let lines = [];            // [{id,text,fallbackText,fontName,color,colorLabel,sizePercent,align,spacing}]
  let activeLineId = null;   // aktuell aktivi Zeile
  let draw = null;

  let desiredWidthCm = null;
  let desiredHeightCm = null;
  let lastEdited = null;
  let updatingInputs = false;

  // Preset apply state
  let pendingPreset = null;

  // -------------------------
  // HELPERS
  // -------------------------
  const $ = (sel, root=document) => root.querySelector(sel);
  const $$ = (sel, root=document) => Array.from(root.querySelectorAll(sel));

  function uid() {
    return Date.now().toString() + Math.random().toString(16).slice(2);
  }

  function getLineById(id) {
    return lines.find(l => l.id === id) || null;
  }

  function getLineIndexById(id) {
    return lines.findIndex(l => l.id === id);
  }

  function safeFloat(v, fallback) {
    const n = parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : fallback;
  }

  function normalizeFontName(name) {
    if (!name) return null;
    return String(name).split(',')[0].trim().replace(/^["']|["']$/g, '');
  }

  function getInitialFontFromPanel() {
    const panel = document.getElementById('line-settings-panel');
    if (!panel) return null;

    const el = panel.querySelector('.wunschtext-line-font-input');
    if (!el) return null;

    const v = (el.value || '').trim();
    if (v) return normalizeFontName(v);

    const dt = (el.dataset && el.dataset.text) ? String(el.dataset.text).trim() : '';
    if (dt) return normalizeFontName(dt);

    const dtAttr = String(el.getAttribute('data-text') || '').trim();
    if (dtAttr) return normalizeFontName(dtAttr);

    const fam = getComputedStyle(el).fontFamily;
    return normalizeFontName(fam);
  }

  // ✅ NEU: Farbname aus Dynamic-Select holen (Header-Text oder Option-Text)
  function getColorLabelFromDynamicSelect(colorInputEl, value) {
    if (!colorInputEl) return null;

    const root =
      colorInputEl.closest('.dynamic-select') ||
      colorInputEl.closest('[class*="dynamic-select"]') ||
      document;

    // 1) Header-Label (zeigt normalerweise de gewählte Farbname)
    const headerText = root.querySelector('.dynamic-select-header .dynamic-select-option-text');
    if (headerText && headerText.textContent && headerText.textContent.trim()) {
      return headerText.textContent.trim();
    }

    // 2) Option anhand vom value finde (robust, ohne fragile selector-escapes)
    const v = String(value ?? '').trim();
    if (!v) return null;

    const options = root.querySelectorAll('.dynamic-select-option, [data-value], [color-value], [option-value], option');
    for (const opt of options) {
      const dv = opt.getAttribute('data-value');
      const cv = opt.getAttribute('color-value');
      const ov = opt.getAttribute('option-value');
      const vv = opt.getAttribute('value');

      if (dv === v || cv === v || ov === v || vv === v) {
        const t = (opt.textContent || '').trim();
        if (t) return t;
      }
    }

    return null;
  }

  // -------------------------
  // INIT
  // -------------------------
  document.addEventListener('DOMContentLoaded', function() {
    const svgEl = document.getElementById('text-preview');
    if (!svgEl) {
      console.warn('[Wunschtext] SVG #text-preview nicht gefunden.');
      return;
    }
    draw = SVG(svgEl);

    initFontConfigAndLoad();
    initGlobalSizeControls();
    initDelegatedLineControls();
    initSettingsPanelListeners();
    initPresetRadios();

    // erste Zeile: Input bleibt leer – Vorschau zeigt data-text/placeholder
    addLine({
      text: '',
      fallbackText: '',
      sizePercent: 100,
      color: '#000000',
      align: 'center',
      fontName: currentFontName
    });
  });

  // -------------------------
  // FONTS
  // -------------------------
  function initFontConfigAndLoad() {
    const select = document.getElementById('font-select');
    if (!select) return;

    FONT_CONFIG = {};
    loadedFonts = {};

    Array.from(select.options).forEach(opt => {
      const name = opt.value;
      const url = opt.dataset.font;
      if (name && url) FONT_CONFIG[name] = url;
    });

    const panelFont = getInitialFontFromPanel();
    if (panelFont && FONT_CONFIG[panelFont]) {
      currentFontName = panelFont;
      if (select.value !== panelFont) select.value = panelFont;
    } else {
      currentFontName = select.value || Object.keys(FONT_CONFIG)[0] || null;
    }

    Object.entries(FONT_CONFIG).forEach(([name, url]) => {
      opentype.load(url, function(err, font) {
        if (err) {
          console.error('[Wunschtext] Font load error:', name, err);
          return;
        }
        loadedFonts[name] = font;
        renderPreview();
      });
    });
  }

  // -------------------------
  // GLOBAL SIZE INPUTS (optional)
  // -------------------------
  function initGlobalSizeControls() {
    const widthInput = document.getElementById('target-width-cm');
    const heightInput = document.getElementById('target-height-cm');

    function attach(input, kind) {
      if (!input) return;
      ['change', 'blur'].forEach(evt => {
        input.addEventListener(evt, () => {
          if (updatingInputs) return;
          const v = safeFloat(input.value, null);

          if (kind === 'width') {
            desiredWidthCm = v;
            lastEdited = 'width';
          } else {
            desiredHeightCm = v;
            lastEdited = 'height';
          }
          renderPreview();
        });
      });
    }

    attach(widthInput, 'width');
    attach(heightInput, 'height');

    $$('.size-step-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetKind = btn.dataset.target;
        const dir = btn.dataset.dir === '-' ? -1 : 1;
        const input = targetKind === 'width' ? widthInput : heightInput;
        if (!input) return;

        const step = safeFloat(input.step, 0.1);
        let cur = safeFloat(input.value, 0);

        cur = cur + dir * step;
        if (input.min !== '') cur = Math.max(cur, safeFloat(input.min, cur));
        if (input.max !== '') cur = Math.min(cur, safeFloat(input.max, cur));

        input.value = cur.toFixed(1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  // -------------------------
  // LINES UI
  // -------------------------
  function initDelegatedLineControls() {
    const addBtn = document.getElementById('add-line-btn');
    if (addBtn) {
      addBtn.addEventListener('click', function(e) {
        e.preventDefault();
        addLine();
      });
    }

    document.addEventListener('click', function(e) {
      const removeBtn = e.target.closest('.wunschtext-line-remove');
      if (removeBtn) {
        e.preventDefault();
        const row = removeBtn.closest('.line-row');
        if (!row) return;
        removeLine(row.dataset.lineId);
        return;
      }

      // ✅ DUPLICATE
      const duplicateBtn = e.target.closest('.duplicate-button');
      if (duplicateBtn) {
        e.preventDefault();
        const row = duplicateBtn.closest('.line-row');
        if (!row) return;
        duplicateLine(row.dataset.lineId);
        return;
      }

      const row = e.target.closest('.line-row');
      if (row && row.dataset.lineId) {
        setActiveLine(row.dataset.lineId);
      }
    });

    document.addEventListener('input', function(e) {
      const input = e.target.closest('.wunschtext-line-text');
      if (!input) return;

      const row = input.closest('.line-row');
      if (!row) return;

      const line = getLineById(row.dataset.lineId);
      if (!line) return;

      line.text = input.value;
      renderPreview();
    });
  }

  function addLine(initial = {}) {
    const id = uid();

    const line = {
      id,
      text: (typeof initial.text === 'string') ? initial.text : '',
      fallbackText: (initial.fallbackText !== undefined ? initial.fallbackText : ''),
      fontName: initial.fontName || currentFontName || Object.keys(FONT_CONFIG)[0] || null,
      color: initial.color || '#000000',

      // ✅ NEU: default label versuchen aus UI zu holen, sonst fallback uf color-value
      colorLabel: (typeof initial.colorLabel === 'string' && initial.colorLabel.trim())
        ? initial.colorLabel.trim()
        : null,

      sizePercent: typeof initial.sizePercent === 'number' ? initial.sizePercent : 100,
      align: initial.align || 'center',
      spacing: typeof initial.spacing === 'number' ? initial.spacing : 1.0
    };

    if (!line.colorLabel) {
      const panel = document.getElementById('line-settings-panel');
      const colorInput = panel ? panel.querySelector('.wunschtext-line-color-input') : null;
      const label = getColorLabelFromDynamicSelect(colorInput, line.color);
      line.colorLabel = label || line.color;
    }

    lines.push(line);
    renderLineRow(line);
    renumberPropertyNames();
    rebuildTabs();
    setActiveLine(id);
    renderPreview();
  }

  function removeLine(lineId) {
    const idx = getLineIndexById(lineId);
    if (idx === -1) return;

    const row = document.querySelector('.line-row[data-line-id="' + lineId + '"]');
    if (row) row.remove();

    lines.splice(idx, 1);

    if (activeLineId === lineId) {
      const next = lines[idx] || lines[idx - 1] || lines[0] || null;
      activeLineId = next ? next.id : null;
    }

    renumberPropertyNames();
    rebuildTabs();
    if (activeLineId) syncPanelFromLine(getLineById(activeLineId));
    renderPreview();
  }

  // ✅ DUPLICATE LINE
  function duplicateLine(lineId) {
    const idx = getLineIndexById(lineId);
    if (idx === -1) return;

    const original = lines[idx];
    if (!original) return;

    const newId = uid();

    const copy = {
      id: newId,
      text: original.text || '',
      fallbackText: original.fallbackText || '',
      fontName: original.fontName,
      color: original.color,

      // ✅ NEU: label mitkopiere
      colorLabel: original.colorLabel || original.color,

      sizePercent: original.sizePercent,
      align: original.align,
      spacing: original.spacing
    };

    // Array: direkt nach Original einfügen
    lines.splice(idx + 1, 0, copy);

    // DOM: direkt nach Original-Row einfügen
    const originalRow = document.querySelector('.line-row[data-line-id="' + lineId + '"]');
    renderLineRow(copy, originalRow);

    renumberPropertyNames();
    rebuildTabs();
    setActiveLine(newId);
    renderPreview();
  }

  function renderLineRow(line, insertAfterEl = null) {
    const container = document.getElementById('lines-container');
    const template = document.getElementById('line-template');
    if (!container || !template) return;

    const fragment = template.content.cloneNode(true);
    const row = fragment.querySelector('.line-row');
    if (!row) {
      console.warn('[Wunschtext] line-template braucht .line-row als Wrapper.');
      return;
    }

    row.dataset.lineId = line.id;

    const textInput = row.querySelector('.wunschtext-line-text');
    if (textInput) {
      const fb =
        (textInput.dataset && textInput.dataset.text) ||
        textInput.getAttribute('data-text') ||
        textInput.getAttribute('placeholder') ||
        (line.fallbackText && String(line.fallbackText).trim()) ||
        'Wunschtext';

      line.fallbackText = String(fb || 'Wunschtext');

      textInput.value = line.text || '';
      textInput.placeholder = line.fallbackText;
      textInput.setAttribute('data-text', line.fallbackText);
    }

    const fontH = row.querySelector('.line-prop-font');
    const colH  = row.querySelector('.line-prop-color');
    const sizeH = row.querySelector('.line-prop-size');
    const spcH  = row.querySelector('.line-prop-spacing');
    const algH  = row.querySelector('.line-prop-align');

    if (fontH) fontH.value = line.fontName || '';

    // ✅ WICHTIG: Hidden = Farbname (Label), Preview bleibt via line.color
    if (colH)  colH.value  = line.colorLabel || line.color;

    if (sizeH) sizeH.value = String(line.sizePercent);
    if (spcH)  spcH.value  = String(line.spacing);
    if (algH)  algH.value  = line.align;

    // Optional: direkt nach einer bestehenden Row einfügen (für Duplizieren)
    if (insertAfterEl && insertAfterEl.parentNode === container) {
      container.insertBefore(fragment, insertAfterEl.nextSibling);
    } else {
      container.appendChild(fragment);
    }
  }

  function renumberPropertyNames() {
    const rows = $$('.line-row', document.getElementById('lines-container') || document);
    rows.forEach((row, i) => {
      const n = i + 1;

      const text = row.querySelector('.wunschtext-line-text');
      const font = row.querySelector('.line-prop-font');
      const col  = row.querySelector('.line-prop-color');
      const size = row.querySelector('.line-prop-size');
      const spc  = row.querySelector('.line-prop-spacing');
      const alg  = row.querySelector('.line-prop-align');

      if (text) text.name = `properties[Text${n}]`;
      if (font) font.name = `properties[Schrifart${n}]`;
      if (col)  col.name  = `properties[Folienfarbe${n}]`;
      if (size) size.name = `properties[_Textgroesse${n}]`;
      if (spc)  spc.name  = `properties[_AbstandOben${n}]`;
      if (alg)  alg.name  = `properties[_Ausrichtung${n}]`;
    });
  }

  // -------------------------
  // TABS
  // -------------------------
  function rebuildTabs() {
    const tabs = document.getElementById('line-tabs');
    if (!tabs) return;

    tabs.innerHTML = '';

    lines.forEach((line, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'line-tab-btn';
      btn.dataset.lineId = line.id;
      btn.textContent = `Zeile ${i + 1}`;
      if (line.id === activeLineId) btn.classList.add('is-active');

      btn.addEventListener('click', () => setActiveLine(line.id));
      tabs.appendChild(btn);
    });
  }

  function setActiveLine(lineId) {
    if (!lineId) return;
    activeLineId = lineId;

    $$('.line-row').forEach(r => r.classList.toggle('is-active', r.dataset.lineId === lineId));
    $$('.line-tab-btn').forEach(b => b.classList.toggle('is-active', b.dataset.lineId === lineId));

    const line = getLineById(lineId);
    if (line) syncPanelFromLine(line);
  }

  // -------------------------
  // SETTINGS PANEL
  // -------------------------
  function initSettingsPanelListeners() {
    const panel = document.getElementById('line-settings-panel');
    if (!panel) return;

    if (window.initAllDynamicSelects) {
      window.initAllDynamicSelects(panel);
    }

    panel.addEventListener('change', function(e) {
      if (!activeLineId) return;
      const line = getLineById(activeLineId);
      if (!line) return;

      const fontInput  = e.target.closest('.wunschtext-line-font-input');
      const colorInput = e.target.closest('.wunschtext-line-color-input');
      const sizeInput  = e.target.closest('.wunschtext-line-size-input');
      const alignInput = e.target.closest('.wunschtext-line-align-input');
      const spcInput   = e.target.closest('.wunschtext-line-spacing-input');

      if (fontInput) {
        const v = (fontInput.value || '').trim();
        const dt = (fontInput.dataset && fontInput.dataset.text) ? String(fontInput.dataset.text).trim() : '';
        const dtAttr = String(fontInput.getAttribute('data-text') || '').trim();
        const wanted = normalizeFontName(v || dt || dtAttr);

        if (wanted && (FONT_CONFIG[wanted] || loadedFonts[wanted])) {
          line.fontName = wanted;
        } else if (v) {
          line.fontName = v;
        }

        writeLineHidden(activeLineId, 'font', line.fontName);
        renderPreview();
      }

      if (colorInput) {
        // ✅ Preview: bleibt Hex/Value
        const c = colorInput.getAttribute('color-value') || colorInput.value || line.color;
        line.color = c;

        // ✅ Hidden: Farbname (Label)
        const label = getColorLabelFromDynamicSelect(colorInput, c);
        line.colorLabel = label || c;

        // Hier wird jetzt der NAME gspeicheret
        writeLineHidden(activeLineId, 'color', line.colorLabel);

        renderPreview();
      }

      if (sizeInput) {
        const v = safeFloat(sizeInput.value, line.sizePercent);
        line.sizePercent = v;
        writeLineHidden(activeLineId, 'size', String(v));
        renderPreview();
      }

      if (alignInput) {
        line.align = alignInput.value || line.align;
        writeLineHidden(activeLineId, 'align', line.align);
        renderPreview();
      }

      if (spcInput) {
        let v = safeFloat(spcInput.value, line.spacing);
        v = Math.max(0.5, Math.min(2.5, v));
        line.spacing = v;
        writeLineHidden(activeLineId, 'spacing', String(v));
        renderPreview();
      }
    });
  }

  function syncPanelFromLine(line) {
    const panel = document.getElementById('line-settings-panel');
    if (!panel || !line) return;

    const fontInput  = panel.querySelector('.wunschtext-line-font-input');
    const colorInput = panel.querySelector('.wunschtext-line-color-input');
    const sizeInput  = panel.querySelector('.wunschtext-line-size-input');
    const alignInput = panel.querySelector('.wunschtext-line-align-input');
    const spcInput   = panel.querySelector('.wunschtext-line-spacing-input');

    setPanelValue(panel, fontInput,  line.fontName || '', 'font');
    setPanelValue(panel, colorInput, line.color, 'color'); // ✅ Panel bleibt mit value (Hex)
    setPanelValue(panel, sizeInput,  String(line.sizePercent), 'size');
    setPanelValue(panel, alignInput, line.align, 'align');
    setPanelValue(panel, spcInput,   String(line.spacing), 'spacing');

    // ✅ Hidden immer korrekt halten (Name)
    writeLineHidden(line.id, 'color', line.colorLabel || line.color);
  }

  function setPanelValue(panel, input, value, kind) {
    if (!panel || !input) return;

    input.value = value;
    if (kind === 'color') input.setAttribute('color-value', value);

    const root = input.closest('.dynamic-select') || input.closest('[class*="dynamic-select"]') || panel;
    const opt =
      root.querySelector(`[data-value="${cssEscape(value)}"]`) ||
      root.querySelector(`[value="${cssEscape(value)}"]`) ||
      root.querySelector(`[color-value="${cssEscape(value)}"]`) ||
      root.querySelector(`[option-value="${cssEscape(value)}"]`);

    if (opt && typeof opt.click === 'function') opt.click();
    else input.dispatchEvent(new Event('change', { bubbles: true }));

    const headerText = root.querySelector('.dynamic-select-header .dynamic-select-option-text');
    if (headerText) headerText.textContent = (opt && opt.textContent) ? opt.textContent.trim() : value;
  }

  function cssEscape(s) {
    return String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  function writeLineHidden(lineId, kind, value) {
    const row = document.querySelector('.line-row[data-line-id="' + lineId + '"]');
    if (!row) return;

    if (kind === 'font')   { const el = row.querySelector('.line-prop-font');    if (el) el.value = value; }
    if (kind === 'color')  { const el = row.querySelector('.line-prop-color');   if (el) el.value = value; }
    if (kind === 'size')   { const el = row.querySelector('.line-prop-size');    if (el) el.value = value; }
    if (kind === 'spacing'){ const el = row.querySelector('.line-prop-spacing'); if (el) el.value = value; }
    if (kind === 'align')  { const el = row.querySelector('.line-prop-align');   if (el) el.value = value; }
  }

  // -------------------------
  // PREVIEW
  // -------------------------
  function transformPath(path, scale, dx, dy) {
    const commands = path.commands || [];
    commands.forEach(cmd => {
      if ('x'  in cmd) cmd.x  = cmd.x * scale  + dx;
      if ('y'  in cmd) cmd.y  = cmd.y * scale  + dy;
      if ('x1' in cmd) cmd.x1 = cmd.x1 * scale + dx;
      if ('y1' in cmd) cmd.y1 = cmd.y1 * scale + dy;
      if ('x2' in cmd) cmd.x2 = cmd.x2 * scale + dx;
      if ('y2' in cmd) cmd.y2 = cmd.y2 * scale + dy;
    });
  }

  function renderPreview() {
    if (!draw) return;

    draw.clear();

    if (!lines.length) {
      draw.viewbox(0, 0, 10, 10);
      updateSizeInfo(null, null);
      return;
    }

    const tempEntries = [];
    let maxWidth = 0;

    lines.forEach(line => {
      const t = (line.text && line.text.trim().length) ? line.text : (line.fallbackText || 'Wunschtext');
      const text = t || ' ';

      const fontName = line.fontName || currentFontName;
      const font = loadedFonts[fontName];
      if (!font) return;

      const sizePercent = typeof line.sizePercent === 'number' ? line.sizePercent : 100;
      const fontSize = BASE_FONT_SIZE * (sizePercent / 100);

      const path = font.getPath(text, 0, 0, fontSize);
      const bb = path.getBoundingBox();
      const lineHeightPx = (bb.y2 - bb.y1) || fontSize;

      const width = bb.x2 - bb.x1;
      if (width > maxWidth) maxWidth = width;

      tempEntries.push({
        path, bb, lineHeightPx,
        color: line.color,             // ✅ preview = wie vorher
        align: line.align || 'center',
        spacing: line.spacing || 1.0
      });
    });

    if (!tempEntries.length || maxWidth <= 0) {
      draw.viewbox(0, 0, 10, 10);
      updateSizeInfo(null, null);
      return;
    }

    const pathEntries = [];
    let currentY = 0;
    let prevLineHeightPx = 0;

    const refLeft = 0;
    const refRight = maxWidth;
    const refCenter = maxWidth / 2;

    tempEntries.forEach((entry, index) => {
      const { path, bb, lineHeightPx, color, align, spacing } = entry;

      if (index === 0) currentY = 0;
      else currentY += prevLineHeightPx * LINE_SPACING_BASE * (spacing || 1);

      prevLineHeightPx = lineHeightPx;

      const lineLeft = bb.x1;
      const lineRight = bb.x2;
      const lineCenter = (bb.x1 + bb.x2) / 2;

      let shiftX = 0;
      if (align === 'left') shiftX = refLeft - lineLeft;
      else if (align === 'right') shiftX = refRight - lineRight;
      else shiftX = refCenter - lineCenter;

      const shiftY = currentY - bb.y1;

      (path.commands || []).forEach(cmd => {
        if ('x' in cmd)  cmd.x  += shiftX;
        if ('y' in cmd)  cmd.y  += shiftY;
        if ('x1' in cmd) cmd.x1 += shiftX;
        if ('y1' in cmd) cmd.y1 += shiftY;
        if ('x2' in cmd) cmd.x2 += shiftX;
        if ('y2' in cmd) cmd.y2 += shiftY;
      });

      pathEntries.push({ path, color });
    });

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    pathEntries.forEach(entry => {
      const bb = entry.path.getBoundingBox();
      if (bb.x1 < minX) minX = bb.x1;
      if (bb.y1 < minY) minY = bb.y1;
      if (bb.x2 > maxX) maxX = bb.x2;
      if (bb.y2 > maxY) maxY = bb.y2;
    });

    const rawWidth  = (maxX - minX);
    const rawHeight = (maxY - minY);

    const rawWidthCm  = rawWidth  * PX_TO_CM;
    const rawHeightCm = rawHeight * PX_TO_CM;

    let scale = 1;

    if (rawWidthCm > 0 && rawHeightCm > 0) {
      if (lastEdited === 'width' && desiredWidthCm && desiredWidthCm > 0) scale = desiredWidthCm / rawWidthCm;
      else if (lastEdited === 'height' && desiredHeightCm && desiredHeightCm > 0) scale = desiredHeightCm / rawHeightCm;
      else if (desiredWidthCm && desiredWidthCm > 0) scale = desiredWidthCm / rawWidthCm;
      else if (desiredHeightCm && desiredHeightCm > 0) scale = desiredHeightCm / rawHeightCm;

      const minTotalHeightCm = lines.length * MIN_LINE_HEIGHT_CM;
      const minScale = minTotalHeightCm > 0 ? (minTotalHeightCm / rawHeightCm) : 0;

      const maxScaleWidth  = MAX_WIDTH_CM  / rawWidthCm;
      const maxScaleHeight = MAX_HEIGHT_CM / rawHeightCm;
      let maxScale = Math.min(maxScaleWidth, maxScaleHeight);
      if (!isFinite(maxScale) || maxScale <= 0) maxScale = Infinity;

      if (scale <= 0 || !isFinite(scale)) scale = 1;
      if (scale < minScale) scale = minScale;
      if (scale > maxScale) scale = maxScale;
    }

    const finalWidthCm  = rawWidthCm  * scale;
    const finalHeightCm = rawHeightCm * scale;

    syncSizeInputs(finalWidthCm, finalHeightCm);
    updateSizeInfo(finalWidthCm, finalHeightCm);

    let previewScale = 1;
    const PREVIEW_MAX_W = 800;
    const PREVIEW_MAX_H = 400;

    if (rawWidth > 0 && rawHeight > 0) {
      const sx = PREVIEW_MAX_W / rawWidth;
      const sy = PREVIEW_MAX_H / rawHeight;
      previewScale = Math.min(sx, sy, 1);
    }

    const widthScaled  = rawWidth  * previewScale;
    const heightScaled = rawHeight * previewScale;

    const offsetX = -(minX * previewScale);
    const offsetY = -(minY * previewScale);

    const group = draw.group();
    pathEntries.forEach(entry => {
      transformPath(entry.path, previewScale, offsetX, offsetY);
      group.path(entry.path.toPathData(2)).fill(entry.color || '#000000');
    });

    draw.viewbox(0, 0, widthScaled, heightScaled);

    const svgEl = document.getElementById('text-preview');
    if (svgEl) {
      svgEl.setAttribute('width', widthScaled);
      svgEl.setAttribute('height', heightScaled);
    }
  }

  function syncSizeInputs(wCm, hCm) {
    const widthInput = document.getElementById('target-width-cm');
    const heightInput = document.getElementById('target-height-cm');
    if (!widthInput || !heightInput || !wCm || !hCm) return;

    updatingInputs = true;
    widthInput.value = wCm.toFixed(1);
    heightInput.value = hCm.toFixed(1);
    desiredWidthCm = wCm;
    desiredHeightCm = hCm;
    updatingInputs = false;
  }

  function updateSizeInfo(wCm, hCm) {
    const el = document.getElementById('size-info');
    if (!el) return;
    if (!wCm || !hCm) { el.textContent = '–'; return; }
    el.textContent = wCm.toFixed(1) + ' × ' + hCm.toFixed(1) + ' cm';
  }

  // -------------------------
  // PRESETS (Radio + Apply)
  // -------------------------
  function initPresetRadios() {
    const root = document.getElementById('wunschtext-presets');
    const applyBtn = document.getElementById('apply-preset-btn');
    if (!root || !applyBtn) return;

    applyBtn.disabled = true;

    root.addEventListener('change', (e) => {
      const radio = e.target.closest('input[type="radio"][name="wunschtext_preset"]');
      if (!radio) return;

      const raw = radio.getAttribute('data-preset');
      if (!raw) return;

      try {
        pendingPreset = JSON.parse(raw);
        applyBtn.disabled = false;
      } catch (err) {
        console.warn('[Wunschtext] Preset JSON ungültig:', err);
        pendingPreset = null;
        applyBtn.disabled = true;
      }
    });

    applyBtn.addEventListener('click', () => {
      if (!pendingPreset) return;
      applyPreset(pendingPreset);
    });
  }

  function applyPreset(preset) {
    if (!preset || !Array.isArray(preset.lines)) return;

    const linesCfg = preset.lines;

    lines = [];
    activeLineId = null;

    const container = document.getElementById('lines-container');
    if (container) container.innerHTML = '';

    linesCfg.forEach(cfg => {
      const presetText = (cfg.text != null ? String(cfg.text) : 'Wunschtext');

      addLine({
        text: presetText,
        fallbackText: presetText,
        fontName: cfg.fontName || currentFontName,
        color: cfg.color || '#000000',

        // optional falls du im Preset scho de Name hesch:
        colorLabel: cfg.colorLabel || '',

        sizePercent: (typeof cfg.sizePercent === 'number') ? cfg.sizePercent : 100,
        spacing: (typeof cfg.spacing === 'number') ? cfg.spacing : 1.0,
        align: cfg.align || 'center'
      });
    });

    if (lines[0]) setActiveLine(lines[0].id);
    renderPreview();
  }

  window.applyWunschtextPreset = applyPreset;

})();
